import { gql, useQuery } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useExtractData custom hook for export/report data operations.
 * - Provides methods to generate export files and fetch reports using Apollo Client and GraphQL.
 * - Handles queries for exporting data, fetching user reports, and generating presigned URLs.
 * - Ensures UI can trigger and retrieve export/report data as needed.
 */

const EXTRACT_DATA_QUERIES = {
  // Query to export data for a given table/module
  GET_EXPORT_DATA: gql`
    query GetExportData($table_name: String!, $filters: FilterInput) {
      GetExportData(table_name: $table_name, filters: $filters) {
        report_id
        report_name
        report_status
        file_path
        created_on
        updated_on
      }
    }
  `,

  // Query to fetch reports by resource ID (e.g., user or module)
  GET_REPORTS_BY_RESOURCE_ID: gql`
    query GetReportsByResourceId($resource_id: ID!) {
      GetReportsByResourceId(resource_id: $resource_id) {
        report_id
        report_name
        report_status
        file_path
        created_on
        updated_on
      }
    }
  `,

  // Query to get a presigned URL for file download
  GET_PRESIGNED_URL: gql`
    query GetPresignedURL($object_name: String!) {
      GetPresignedURL(object_name: $object_name)
    }
  `,
};

/**
 * useExtractData hook: Provides a method to generate export files.
 * @returns {Object} generateFile method
 */
export const useExtractData = () => {
  /**
   * Generates an export file for a given module/table and filters.
   * @param {string} moduleName - Name of the module/table.
   * @param {Object} filters - Filter object for export.
   * @returns {Promise<Object|null>} Export data or null on error.
   */
  const generateFile = async (moduleName, filters) => {
    try {
      console.log("The moduleName is", moduleName, filters);
      const { data } = await client.query({
        query: EXTRACT_DATA_QUERIES.GET_EXPORT_DATA,
        variables: { table_name: moduleName, filters },
        fetchPolicy: "network-only",
      });
      return data;
    } catch (err) {
      console.log("The error happening while generating file", err);
      return null;
    }
  };

  return {
    generateFile,
  };
};

/**
 * useUserReport hook: Fetches reports for a given resource (e.g., user).
 * @param {string|number} resourceId - Resource ID to fetch reports for.
 * @returns {Object} reports, loading, error, refetch
 */
export const useUserReport = (resourceId) => {
  const { data, loading, error, refetch } = useQuery(
    EXTRACT_DATA_QUERIES.GET_REPORTS_BY_RESOURCE_ID,
    {
      variables: { resource_id: resourceId },
      fetchPolicy: "network-only",
    }
  );

  return {
    reports: data?.GetReportsByResourceId || [],
    loading,
    error,
    refetch,
  };
};

/**
 * usePresignedURL hook: Provides a method to generate a presigned URL for file download.
 * @returns {Object} generatePresignedURL method
 */
export const usePresignedURL = () => {
  /**
   * Generates a presigned URL for a given object/file name.
   * @param {string} objectName - Name of the file/object.
   * @returns {Promise<Object|null>} Presigned URL data or null on error.
   */
  const generatePresignedURL = async (objectName) => {
    try {
      const { data } = await client.query({
        query: EXTRACT_DATA_QUERIES.GET_PRESIGNED_URL,
        variables: { object_name: objectName },
        fetchPolicy: "network-only",
      });
      return data;
    } catch (err) {
      console.log("The error happening while generating presigned url", err);
      return null;
    }
  };
  return {
    generatePresignedURL,
  };
};
